import type { PortfolioSummary } from "creso-core/types";
import {
  cn,
  ChartTooltipContent,
  Popover,
  PopoverContent,
  PopoverTrigger,
  GappedChart,
} from "web-design-system";
import { Check, ChevronDown } from "lucide-react";
import { convertToCurrency, formatAsPercentage } from "creso-core/utils";
import { useState } from "react";
import { NoDataAnimation } from "../indicator";
import { ValueVector } from "../primitives";

export const PortfolioSummaryComponent: React.FC<{
  summary?: PortfolioSummary;
  isLoading?: boolean;
}> = ({ summary }) => {
  return <PortfolioAllocationChart portfolioSummary={summary} />;
};

function PortfolioAllocationChart({
  portfolioSummary,
}: {
  portfolioSummary?: PortfolioSummary;
}) {
  type XirrType = "active" | "lifetime";
  const {
    invested_value,
    current_value,
    profit_loss,
    xirr,
    active_xirr,
    lifetime_xirr,
    absolute_return_pct,
    previous_day_change,
    previous_day_value,
    previous_day_return_pct,
  } = portfolioSummary || {};

  let total_invested_computed = Number(invested_value);
  let current_value_computed = Number(current_value);
  let profit_loss_computed = Number(profit_loss);
  let xirr_computed = Number(xirr);
  const active_xirr_computed = Number(active_xirr);
  const lifetime_xirr_computed = Number(lifetime_xirr);
  let absolute_return_pct_computed = Number(absolute_return_pct);
  const previous_day_change_computed = Number(previous_day_change);
  const previous_day_value_computed = Number(previous_day_value);

  total_invested_computed = Math.round(total_invested_computed);
  current_value_computed = Math.round(current_value_computed);
  profit_loss_computed = parseFloat(profit_loss_computed.toFixed(2));
  xirr_computed = parseFloat(xirr_computed.toFixed(2));
  absolute_return_pct_computed = parseFloat(
    absolute_return_pct_computed.toFixed(2)
  );

  const previous_day_return_pct_computed = previous_day_return_pct
    ? parseFloat(Number(previous_day_return_pct).toFixed(2))
    : previous_day_value_computed === 0
      ? 0
      : parseFloat(
          (
            (previous_day_change_computed / previous_day_value_computed) *
            100
          ).toFixed(2)
        );

  const isReturnPositive = absolute_return_pct_computed > 0;
  const isLossMaking = absolute_return_pct_computed < 0;

  const activeXirrValue = Number.isFinite(active_xirr_computed)
    ? active_xirr_computed
    : xirr_computed;
  const lifetimeXirrValue = Number.isFinite(lifetime_xirr_computed)
    ? lifetime_xirr_computed
    : xirr_computed;
  const [selectedXirrType, setSelectedXirrType] = useState<XirrType>(
    Number.isFinite(active_xirr_computed) ? "active" : "lifetime"
  );
  const [isXirrPopoverOpen, setIsXirrPopoverOpen] = useState(false);
  const selectedXirrValue =
    selectedXirrType === "active" ? activeXirrValue : lifetimeXirrValue;

  if (
    !current_value_computed ||
    Number(current_value_computed) === 0 ||
    Number.isNaN(Number(current_value_computed))
  ) {
    return (
      <div className="flex items-center flex-col border text-sm border-dashed border-border rounded-md p-4 text-muted-foreground justify-center w-full h-34">
        <NoDataAnimation loop={false} height={100} width={100} />
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg px-4 py-5 bg-card flex flex-col gap-5">
      <div className="w-full flex flex-col gap-4">
        <GappedChart
          segments={
            isLossMaking
              ? [
                  {
                    key: "CurrentValue",
                    value: current_value_computed,
                    color: "var(--chart-1)",
                    label: "Current Value",
                  },
                  {
                    key: "Returns",
                    value: Math.abs(profit_loss_computed),
                    color: "var(--chart-3)",
                    label: "Returns",
                  },
                ]
              : [
                  {
                    key: "InvestedAmount",
                    value: total_invested_computed,
                    color: "var(--chart-1)",
                    label: "Invested Amount",
                  },
                  {
                    key: "Returns",
                    value: Math.abs(profit_loss_computed),
                    color: "var(--chart-4)",
                    label: "Returns",
                  },
                ]
          }
          tooltipContent={
            <ChartTooltipContent
              hideLabel
              formatter={(value, name, item) => (
                <div className="flex flex-1 items-center justify-between leading-none">
                  <div className="flex items-center gap-2">
                    <div
                      className="size-2.5 shrink-0 rounded-[2px]"
                      style={{ backgroundColor: item.payload?.fill }}
                    />
                    <span className="text-muted-foreground">{name}</span>
                  </div>
                  <span className="text-foreground ml-2 font-medium tabular-nums">
                    {name === "Returns"
                      ? convertToCurrency(profit_loss_computed, 0)
                      : convertToCurrency(Number(value), 0)}
                  </span>
                </div>
              )}
            />
          }
        />
      </div>
      <div
        className={cn(
          "grid grid-cols-2 lg:grid-cols-5 print:grid-cols-5 gap-3 justify-between"
        )}
      >
        <div
          className={cn(
            "flex flex-col px-3 gap-1 col-span-1",
            isLossMaking && "border-l-4 border-chart-1"
          )}
        >
          <span className="text-sm text-muted-foreground">Total Value</span>
          <span className="text-base font-semibold">
            {convertToCurrency(current_value_computed, 0)}
          </span>
        </div>
        <div
          className={cn(
            "flex flex-col px-3 gap-1 col-span-1",
            !isLossMaking && "border-l-4 border-chart-1"
          )}
        >
          <span className="text-sm text-muted-foreground">Invested Amount</span>
          <span className="text-base font-semibold">
            {convertToCurrency(total_invested_computed, 0)}
          </span>
        </div>
        <div
          className={cn(
            "flex flex-col border-l-4 px-3 gap-1 col-span-1",
            isReturnPositive ? "border-chart-4" : "border-chart-3"
          )}
        >
          <span className="text-sm text-muted-foreground">Returns</span>
          <div className="flex flex-row gap-1 items-end">
            <span className="text-base font-semibold">
              {convertToCurrency(profit_loss_computed, 0)}&nbsp;
            </span>
            <ValueVector
              className="text-base font-semibold"
              value={absolute_return_pct_computed}
              formatter={(v) =>
                formatAsPercentage(Math.abs(Number(v)), "N/A", 1)
              }
            />
          </div>
        </div>
        <div className="flex flex-col px-3 gap-1 col-span-1">
          <Popover open={isXirrPopoverOpen} onOpenChange={setIsXirrPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-sm text-muted-foreground w-fit inline-flex items-center gap-1"
              >
                XIRR ({selectedXirrType === "active" ? "Active" : "Lifetime"})
                <ChevronDown className="size-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={8}
              className="rounded-xl bg-background p-0 w-90"
            >
              <div className="p-2 flex flex-col gap-1">
                <button
                  type="button"
                  className="w-full flex items-start hover:bg-accent justify-between text-left p-2"
                  onClick={() => {
                    setSelectedXirrType("active");
                    setIsXirrPopoverOpen(false);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Check
                      className={cn(
                        "size-4 mt-1 text-foreground shrink-0",
                        selectedXirrType !== "active" && "opacity-0"
                      )}
                    />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm">Active XIRR</span>
                      <span className="text-muted-foreground text-xs">
                        Based on current holdings
                      </span>
                    </div>
                  </div>
                  <ValueVector
                    className="text-sm font-semibold"
                    value={activeXirrValue}
                    formatter={(v) =>
                      formatAsPercentage(Math.abs(Number(v)), "N/A", 1)
                    }
                  />
                </button>
                <button
                  type="button"
                  className="w-full flex items-start hover:bg-accent justify-between text-left p-2"
                  onClick={() => {
                    setSelectedXirrType("lifetime");
                    setIsXirrPopoverOpen(false);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Check
                      className={cn(
                        "size-4 mt-1 text-foreground shrink-0",
                        selectedXirrType !== "lifetime" && "opacity-0"
                      )}
                    />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm">Lifetime XIRR</span>
                      <span className="text-muted-foreground text-xs">
                        Based on entire timeline of cash flows
                      </span>
                    </div>
                  </div>
                  <ValueVector
                    className="text-sm font-semibold"
                    value={lifetimeXirrValue}
                    formatter={(v) =>
                      formatAsPercentage(Math.abs(Number(v)), "N/A", 1)
                    }
                  />
                </button>
              </div>
            </PopoverContent>
          </Popover>
          <ValueVector
            className="text-base font-semibold"
            value={selectedXirrValue}
            formatter={(v) => formatAsPercentage(Math.abs(Number(v)), "N/A", 1)}
          />
        </div>
        <div className="flex flex-col px-3 gap-1 col-span-1">
          <span className="text-sm text-muted-foreground">1D Returns</span>
          <div className="flex flex-row gap-1 items-baseline">
            <span className="text-base font-semibold">
              {convertToCurrency(previous_day_change_computed, 0)}
            </span>
            <ValueVector
              className="text-base font-semibold"
              value={previous_day_return_pct_computed}
              formatter={(v) =>
                formatAsPercentage(Math.abs(Number(v)), "N/A", 1)
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
